import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { concat, forkJoin, merge, Observable } from 'rxjs';
import { filter, map, mergeMap, tap } from 'rxjs/operators';
import { AuthService } from 'src/app/auth/auth.service';
import { ApiService } from 'src/app/core/api.service';
import { User } from 'src/app/core/models/user';
import { SystemService } from 'src/app/core/system.service';

export interface Bookmark {
  title: string;
  route: string;
}

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss']
})
export class MenuComponent implements OnInit {
  @Input() user: User
  @Output() logout: EventEmitter<void> = new EventEmitter();

  bookmarks: Bookmark[] = [];
  keyLessonsProgress: string = '';
  advancedLessonsProgress: string = '';

  courseProgress: {keyLessons: any[], advancedLessons: any[]} = {keyLessons: [], advancedLessons: []};

  countryCodes: string[] = ['gb', 'dk', 'de'];
  //countryCodes: string[] = ['gb', 'dk'];

  userBackup: User;
  inEdition: boolean;
  
  constructor(
    private router: Router,
    private translateService: TranslateService,
    private apiService: ApiService,
    private authService: AuthService,
    public systemService: SystemService) { }

  ngOnInit() {

    merge(
      this.systemService.inGameMode$,
      this.systemService.reload$
    )
      .pipe(

        filter((value: boolean) => {
          return   value || value === undefined
        }),
        
        // load bookmarks
        mergeMap(_ => {
          const requests = [
            // load bookmarks
            this.apiService.loadBookmarks(this.user.userid, this.user.language_in_use),  

            // load completed lessons
            this.loadCompletedLessons()
          ]

          return forkJoin([...requests])
        })

      )
      .subscribe(values => {
        // bookmarks
        const bookmarkResult = values[0]
        const bookmarks: any[] = bookmarkResult.zz.z;
        this.bookmarks = !!bookmarks.length 
          ? bookmarks.filter(value => typeof value === 'string').map(value => { return {title: this.getTranslatedBookmark(value), route: 'TODO'} })
          : [];

        // completed lessons
        const completedLessonsResult = values[1];
        this.keyLessonsProgress = completedLessonsResult['keyLessons'].toFixed(2);
        this.advancedLessonsProgress = completedLessonsResult['advancedLessons'].toFixed(2);

        this.loadCourseProgress();

      });

  }

  private loadCompletedLessons(): Observable<any> {
    return this.apiService.loadCompletedLessons(this.user.userid, this.user.language_in_use)
      .pipe(
        map(value => {
          const lessonsResults = value.zz.z[0];
          const numKeyLessons = 11;
          const numAdvancedLessons = 12;

          let keyLessonsResult = 0;
          let patchKeyLessons = {};
          for(var i = 1; i <= numKeyLessons; i++) {
            patchKeyLessons[`basicLesson${i}Complete`] = lessonsResults[`basicLesson${i}Complete`];
            keyLessonsResult += +lessonsResults[`basicLesson${i}Complete`];
          }

          let advancedLessonsResult = 0;
          let patchAdvancedLessons = {};
          for(var i = 1; i <= numAdvancedLessons; i++) {
            patchAdvancedLessons[`advancedLesson${i}Complete`] = lessonsResults[`advancedLesson${i}Complete`];
            advancedLessonsResult += +lessonsResults[`advancedLesson${i}Complete`];
          }
                    
          //update user
          let user = this.authService.user$.value;
          user = {
            ...user,
            ...patchKeyLessons,
            ...patchAdvancedLessons
          }


          console.log('user updated...')
          this.authService.user$.next(user);

          return {
            keyLessons: (keyLessonsResult/numKeyLessons) * 100,
            advancedLessons: (advancedLessonsResult/numAdvancedLessons) * 100
          }
          
        })
      )
  }

  private loadCourseProgress() {
    const user = this.authService.user$.value;

    const numKeyLessons = 8;
    const numAdvancedLessons = 12;

    let keyLessonsResult = [];
    for(var i = 1; i <= numKeyLessons; i++) {
      const completed = +user[`basicLesson${i}Complete`] === 1;
      if(completed) {
        keyLessonsResult.push( {id: i, completed: true });
      }      
    }

    let advancedLessonsResult = [];
    for(var i = 1; i <= numAdvancedLessons; i++) {
      const completed = +user[`advancedLesson${i}Complete`] === 1 ;
      if(completed) {
        advancedLessonsResult.push( {id: i, completed: true });
      }  
    }

    this.courseProgress = {
      keyLessons: keyLessonsResult,
      advancedLessons: advancedLessonsResult
    }

  }

  changeSelectedCountryCode(value: string) {
    
    this.user.language_in_use = value === 'gb' 
      ? 'uk'
      : value;
 
    // setup language to use
    let lang;
    if(this.user.language_in_use === 'uk' ) { lang = 'en'; }
    if(this.user.language_in_use === 'dk' ) { lang = 'da'; }
    if(this.user.language_in_use === 'de' ) { lang = 'de'; }
    this.translateService.use(lang);

  }

  edit() {
    this.userBackup = {...this.user};
    this.inEdition = true;
  }

  save() {
    const { username, password } = this.userBackup;
    const oldValues = { oldEmail: username, oldPass: password };
    
    this.apiService.saveUser(this.user, oldValues)
      .subscribe(_ => this.inEdition = false)    
  }

  cancelEdition() {
    this.user = {...this.userBackup};
    this.userBackup = undefined;

    this.inEdition = false;

    // reset previous language
    this.changeSelectedCountryCode(this.user.language_in_use === 'uk' ? 'gb' : this.user.language_in_use)
  }


  navigateToBookmark(bookmark: string) : string{
    bookmark = this.getTranslatedBookmarkToEN(bookmark);
    const isAdvanced = bookmark.toLowerCase().includes('advanced');
    const splitedBookmark = bookmark.split(/(\s+)/);
    
    let url

    if(isAdvanced) {
      const lessonId = splitedBookmark[splitedBookmark.length - 1];
      url = `advancedlesson/${lessonId}/player`;
      this.router.navigate([url, {time: 5}]);
    }else {
      const lessonId = splitedBookmark[splitedBookmark.length - 5];
      const exercise = splitedBookmark[splitedBookmark.length - 1];
      url = `keylesson/${lessonId}/player`;
      this.router.navigate(['keylesson',lessonId, 'player', {continue: true, exercise: exercise}]);
    }
   

    return url;    
  }

  // get translated to 'da' 
  private getTranslatedBookmark(value: string): string {
    const { language_in_use } = this.authService.user$.value;
    if(language_in_use === 'uk') {return value;}

    if(language_in_use === 'dk') {
      value = value.replace('Basic Lesson', 'Grundlektion');
      value = value.replace('Advanced Lesson', 'Hastighedsøvelse');
    }

    if(language_in_use === 'de') {
      value = value.replace('Basic Lesson', 'Grundlektion');
      value = value.replace('Advanced Lesson', 'Geschwindigkeitsübung');
    }
    
    return value;
  }

  // get translated to 'en'
  private getTranslatedBookmarkToEN(value: string): string {
    const { language_in_use } = this.authService.user$.value;
    if(language_in_use === 'uk') {return value;}

    // try from dk to EN
    value = value.replace('Grundlektion', 'Basic Lesson');
    value = value.replace('Hastighedsøvelse', 'Advanced Lesson');

    // try from de to EN
    value = value.replace('Grundlektion', 'Basic Lesson');
    value = value.replace('Geschwindigkeitsübung', 'Advanced Lesson');

    return value;
  }

  get isUnlockedExam(): boolean {
    return +this.advancedLessonsProgress === 100;
  }

  get encryptedPassword(): string {
    return this.user 
      ? this.user.password.replace(/./g, '*')
      : '';
  }


}
